import { useState } from 'react';
import {
  AUTOMATION_CATALOG,
  type Automation,
  type FlowStep,
  type ToolKind,
} from '@/automations/catalog';
import type { AutomationId } from '@/components/Sidebar';
import { PaymentPrepPanel } from '@/components/PaymentPrepPanel';
import { PAYMENT_CHECK_URL } from '@/payment/payment-steps';
import { Tabs } from '@/components/Tabs';
import { MONTHLY_FMT_TASKS, type MonthlyFmtTask } from '@cortex/ado-task-fmt';

/**
 * 自動化システムの一覧。
 *
 * 画面は 10 個作らず 1 つで賄う。違うのは中身（分類・起動のしかた・実行できること）だけで、
 * 見せ方と操作の流れは共通のため。
 * @graph-connects none
 */
export const AUTOMATIONS: Record<AutomationId, Automation> = AUTOMATION_CATALOG;

/** 工程の種類ごとの見た目。人の確認だけ破線にして「ここは自動ではない」と分かるようにする */
/** @graph-connects none */
const FLOW_CLASS: Record<FlowStep['kind'], string> = {
  start:
    'rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] text-[var(--color-text-primary)]',
  proc: 'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]',
  human:
    'rounded border border-dashed border-[var(--color-kind-closing)] bg-[var(--color-kind-closing-bg)] text-[var(--color-kind-closing)]',
  out: 'rounded-full border border-[var(--color-kind-meeting)] bg-[var(--color-kind-meeting-bg)] text-[var(--color-kind-meeting)]',
};

/** 使用ツールの種類ごとの札 */
/** @graph-connects none */
const TOOL_LABEL: Record<ToolKind, string> = {
  gas: 'GAS',
  sheet: 'シート',
  github: 'GitHub',
  other: 'その他',
};

/** @graph-connects none */
const POLICY_CLASS: Record<Automation['policy'], string> = {
  承認: 'border-[var(--color-kind-closing)] bg-[var(--color-kind-closing-bg)] text-[var(--color-kind-closing)]',
  実行: 'border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] text-[var(--color-doing-text)]',
  なし: 'border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] text-[var(--color-text-muted)]',
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 自動化に紐づく月次経理タスクを引く。`大分類|中分類|タスク名|事業` で突き合わせる
 * @graph-connects none
 */
export function linkedFmtTasks(keys: readonly string[]): MonthlyFmtTask[] {
  return MONTHLY_FMT_TASKS.filter((task) => {
    const middle = task.middleCategory === '' ? 'ー' : task.middleCategory;
    return keys.includes(`${task.majorCategory}|${middle}|${task.title}|${task.business}`);
  });
}

/**
 * 画面の中で切り替えるタブ。
 *
 * 開いてすぐ動かせるよう実行を先頭かつ既定にする。概要には「どういう自動化か」
 * （種類・起動・仕組み・置き換わるタスク）をまとめ、右端に実行の履歴を置く。
 */
/** @graph-connects none */
const TABS = [
  { id: 'run', label: '実行' },
  { id: 'summary', label: '概要' },
  { id: 'log', label: '実行ログ' },
];

/**
 * 実行の進行状態。
 */
type RunState = { label: string; phase: 'confirming' | 'running' | 'done' } | null;

/**
 * AutomationRun の props。
 */
export interface AutomationRunProps {
  /** 表示する自動化システム */
  id: AutomationId;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 自動化システムの画面。実行・概要（仕組みと置き換わるタスク）・実行ログをタブで切り替え、使用ツールへのリンクも並べる
 * @graph-connects linkedFmtTasks [calls] 紐づく月次経理タスクを引く
 * @graph-connects PaymentPrepPanel [calls] 支払準備は専用の手順画面を出す
 */
export function AutomationRun({ id }: AutomationRunProps) {
  const [run, setRun] = useState<RunState>(null);
  const [tab, setTab] = useState('run');
  const automation = AUTOMATIONS[id];
  const linked = linkedFmtTasks(automation.fmtKeys);

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      <header className="mb-4">
        <p className="m-0 text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
          {automation.category}
        </p>
        <h1 className="mt-0.5 mb-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
          {automation.shortName}
        </h1>
        <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">{automation.name}</p>
      </header>

      <Tabs items={TABS} selected={tab} onSelect={setTab} label="自動化の表示切り替え" />

      <div className={tab === 'summary' ? 'mt-4 grid gap-4 lg:grid-cols-2' : 'hidden'}>
        {/* 概要。「誰が何をする自動化か」を先に置く */}
        <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
          <dl className="m-0 grid grid-cols-[6rem_1fr] gap-y-2 text-sm">
            <dt className="text-[var(--color-text-muted)]">種類</dt>
            <dd className="m-0 text-[var(--color-text-primary)]">{automation.type}</dd>
            <dt className="text-[var(--color-text-muted)]">起動</dt>
            <dd className="m-0 text-[var(--color-text-primary)]">{automation.trigger}</dd>
            <dt className="text-[var(--color-text-muted)]">人の関わり</dt>
            <dd className="m-0">
              <span
                className={`inline-block rounded-full border px-2 py-0.5 text-xs ${POLICY_CLASS[automation.policy]}`}
              >
                {automation.policy}
              </span>
            </dd>
            <dt className="text-[var(--color-text-muted)]">前回の実行</dt>
            <dd className="m-0 text-[var(--color-text-muted)]">記録なし</dd>
          </dl>
        </section>

        {/* 仕組み。どこからデータが来て、どこで人が見るのかを 1 列で示す */}
        <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
          <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            仕組み
          </h2>
          <ol className="m-0 list-none space-y-1 p-0">
            {automation.flow.map((step, index) => (
              <li key={step.title}>
                {index === 0 ? null : (
                  <div className="ml-3 h-2 border-l border-[var(--color-border-subtle)]" />
                )}
                <div className={`px-3 py-1.5 text-sm ${FLOW_CLASS[step.kind]}`}>
                  <span className="font-medium">{step.title}</span>
                  {step.tool === undefined ? null : (
                    <span className="ml-2 text-xs opacity-70">{step.tool}</span>
                  )}
                  {step.detail === undefined ? null : (
                    <span className="block text-xs opacity-70">{step.detail}</span>
                  )}
                  {step.resource === undefined ? null : (
                    <span className="block text-xs opacity-70">{step.resource}</span>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {automation.note === undefined ? null : (
            <p className="mt-3 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
              {automation.note}
            </p>
          )}
        </section>
      </div>

      {/* 実行。押せることが無いものは、どこから動かすかを書く */}
      <div className={tab === 'run' ? 'mt-4' : 'hidden'}>
        {/* 支払準備は 8 ステップを順に進める専用の画面（支払業務 GAS を動かす） */}
        {id === 'auto-payment-prep' ? (
          <PaymentPrepPanel />
        ) : id === 'auto-invoice-check' ? (
          // cortex への移行が終わるまでは、動いている SFT Hub の振込前チェックへ案内する
          <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
            <p className="m-0 text-sm leading-relaxed text-[var(--color-text-secondary)]">
              いま ADO Hub へ移行中です。移行が終わるまでは SFT Hub
              の「振込前チェック」で実行してください。
            </p>
            <a
              href={PAYMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-block rounded border border-[var(--color-border-subtle)] px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]"
            >
              振込前チェックを開く（SFT Hub）
            </a>
          </section>
        ) : (
          <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
            {automation.runs.length === 0 ? (
              <p className="m-0 text-sm leading-relaxed text-[var(--color-text-secondary)]">
                この自動化はシート側のメニューか自動トリガーで動かしています。ここから動かしたい
                場合は言ってください（受け口はあります）。
              </p>
            ) : (
              <ul className="m-0 list-none space-y-2 p-0">
                {automation.runs.map((action) => (
                  <li key={action.label}>
                    <button
                      type="button"
                      disabled={run?.phase === 'running'}
                      onClick={() => {
                        setRun({ label: action.label, phase: 'confirming' });
                      }}
                      className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]"
                    >
                      {action.label}
                      {action.parameter === undefined ? null : (
                        <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                          {action.parameter === 'month' ? '対象月を選ぶ' : '対象期間を選ぶ'}
                        </span>
                      )}
                    </button>

                    {run?.label === action.label && run.phase === 'confirming' ? (
                      <div className="mt-2 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm">
                        <p className="m-0 leading-relaxed text-[var(--color-text-primary)]">
                          {action.confirm ?? `${action.label}を実行しますか？`}
                        </p>
                        <div className="mt-2 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setRun(null);
                            }}
                            className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1 text-sm text-[var(--color-text-secondary)]"
                          >
                            取りやめる
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRun({ label: action.label, phase: 'done' });
                            }}
                            className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-medium text-[var(--color-bg-surface)]"
                          >
                            実行する
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {run?.label === action.label && run.phase === 'done' ? (
                      <div className="mt-2 rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-3 py-2 text-sm text-[var(--color-doing-text)]">
                        <p className="m-0 leading-relaxed">
                          この機能はまだ動きません。実行の繋ぎ込み（{automation.type}）は、タスク
                          管理が動くようになった後に行います。
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setRun(null);
                          }}
                          className="mt-2 rounded border border-[var(--color-doing-border)] px-2.5 py-1 text-xs"
                        >
                          閉じる
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      <div className={tab === 'summary' ? 'mt-4 grid gap-4 lg:grid-cols-2' : 'hidden'}>
        {/* 使用ツール。GAS・シート・GitHub など、この自動化の実体へ飛べる場所をまとめる */}
        <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
          <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            使用ツール
          </h2>
          {automation.links.length === 0 ? (
            <p className="m-0 text-sm text-[var(--color-text-muted)]">
              まだリンクを登録していません
            </p>
          ) : (
            <ul className="m-0 list-none space-y-2 p-0 text-sm">
              {automation.links.map((link) => (
                <li key={link.url} className="flex items-baseline gap-2">
                  <span className="w-14 shrink-0 rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-center text-xs text-[var(--color-text-secondary)]">
                    {TOOL_LABEL[link.kind]}
                  </span>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 break-words text-[var(--color-text-secondary)] underline"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 紐づく月次経理タスク。どの作業がこの自動化で置き換わるのかを見えるようにする */}
        <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
          <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            置き換わる月次経理タスク
          </h2>
          {linked.length === 0 ? (
            <p className="m-0 text-sm text-[var(--color-text-muted)]">紐づけはまだありません</p>
          ) : (
            <ul className="m-0 list-none space-y-1.5 p-0 text-sm">
              {linked.map((task) => (
                <li key={task.id} className="text-[var(--color-text-primary)]">
                  {task.title}
                  <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                    {task.dueRule}／{task.assignee}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* 実行ログ。記録は Firestore の automation_runs に残す予定で、いまは枠だけ */}
      <div className={tab === 'log' ? 'mt-4' : 'hidden'}>
        <section className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
          <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            実行ログ
          </h2>
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
                <th className="w-40 py-2 pr-3 font-medium text-[var(--color-text-muted)]">日時</th>
                <th className="py-2 pr-3 font-medium text-[var(--color-text-muted)]">
                  実行した内容
                </th>
                <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">
                  実行した人
                </th>
                <th className="w-20 py-2 pr-3 font-medium text-[var(--color-text-muted)]">結果</th>
                <th className="w-20 py-2 text-right font-medium text-[var(--color-text-muted)]">
                  かかった時間
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  colSpan={5}
                  className="py-10 text-center text-sm text-[var(--color-text-muted)]"
                >
                  まだ記録がありません
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
            実行を繋ぎ込んだ時点から、いつ・誰が・どの操作を動かして成功したかをここに残します
            （保存先は Firestore の実行履歴）。
          </p>
        </section>
      </div>
    </main>
  );
}
